// ---------------------------------------------------------------------------
// Your name and your colour, in one sheet.
//
// **It was two controls and they were the same decision.** The pencil on your
// row opened a rename dialog, and a `<details>` disclosure further down the
// column held a colour picker — so "what do I look like in this room" was
// answered in two places, one of which was a fold nobody opened. This is one
// sheet with both in it, opened from the pencil, from the colour dot on your
// row, or from anywhere else on your own card.
//
// **A dialog rather than an inline editor, and the reason is the seat row.**
// Your row can be a nested row inside a team card, and that card is
// `overflow-hidden` so its coloured fill can round its corners — anything
// anchored to a row is clipped by it. An inline editor also has to swap a
// truncating name for an input in a row that already carries a colour dot, an
// identity slot and the ready marker, on a 370px column: either the field is a
// few characters wide or the row reflows around it while it is open. A dialog
// is the same size wherever your row happens to be, needs no anchoring, and
// moves nothing in the lobby.
//
// It is portalled to the body for the same reason the drag ghost is: no
// ancestor's stacking context or overflow can then clip it. `z-[35]` puts it
// above the chat drawer (30) and under the disconnect sheet (40), because a
// dropped connection is news about the room and this is a form.
//
// **What was tapped decides whether the keyboard comes up.** On a phone,
// focusing the name field summons the keyboard, which covers the colour grid —
// so somebody who tapped the colour dot to change their colour would watch half
// the sheet disappear behind keys they did not ask for. `autoFocusName` is the
// caller's answer to "did they aim at the name": true on a desktop, where
// focusing a field costs nothing, and on a phone only when the pencil itself
// was pressed.
//
// **There is no name-taken error and there must not be one.** The server
// suffixes a duplicate rather than refusing it — a second Isaac becomes
// `Isaac (2)` — so a rename cannot fail for that reason and a message saying it
// might would be describing a rule the game does not have. What can still fail
// is a name that is not a name, and the same `validateDisplayName` the server
// runs answers that here, so the ordinary mistake never costs a round trip.
//
// **Nothing is discarded, and there is no Cancel.** This reverses an earlier
// decision on purpose and the earlier decision was right at the time: the colour
// used to apply on the press, which was changed to save-on-Save because a form
// with a Cancel on it must not have half of itself already done. Isaac's call is
// that a sheet like this should keep what you did — "I feel like it should
// save" — so the Cancel goes, and with it the reason the colour could not be
// instant. One `Done` button, and every way out of the sheet keeps the edit:
// the button, the scrim, and Escape.
//
// **Colour applies on the press. The name is committed when the sheet closes.**
// The two are not treated alike, and the difference is not a hedge — it is what
// a rename COSTS. A colour is a field on your own player; a rename is
// `uniqueDisplayName` running against the whole room, a broadcast to everybody
// in it, and a system line in the log. Saving the name on every keystroke, which
// is the literal reading of "the instant you type a letter", would put `Isaac is
// now Isa` through the room once per letter and suffix against half-typed names
// on the way. So the name goes once, when the sheet is done with, which loses
// nothing: the thing being asked for is that closing the sheet keeps the edit,
// and it does.
//
// **A name that is not a name is the one thing that cannot be kept.** The field
// says why while it is being typed, and on dismiss an invalid name is simply not
// sent — the old one stands. Refusing to close would be the other option and it
// is worse: a scrim tap has to mean out, which is a rule this app now holds
// everywhere. There is nothing lost that could have been kept.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { DISPLAY_NAME_MAX, validateDisplayName } from '@countdown/shared';
import { Button } from '../../../components/primitives/Button.js';
import { ColorPicker } from '../../../components/primitives/ColorPicker.js';
import { swallowNextClick } from '../../../lib/swallowNextClick.js';
import { SectionLabel } from './fields.js';

export function EditPlayerSheet({
  currentName,
  currentColor,
  colorEditable,
  takenColors,
  colorIsSecondary,
  autoFocusName,
  onPickColor,
  onDone,
}: {
  currentName: string;
  /**
   * Your colour, or **null in a room with no colours in it**.
   *
   * Past `COLORED_SEATS` nobody has one — it is all or nothing across the room,
   * see `colorForSeat` — so there is nothing to pick from and no picker. It is
   * not a disabled grid and not a grid of greys: a grey is itself a colour and
   * reads as an identity, and the honest thing to draw is a sentence saying the
   * room goes by name.
   */
  currentColor: string | null;
  /**
   * Whether this player may change it at all. See `playerPermissions`, which is
   * off by default and covers exactly this and opening a team.
   *
   * A different absence from the one above and it says so differently: there is
   * a colour, it is simply not yours to move. Neither of them is a disabled
   * grid — a picker that takes a press and then does nothing is decision 3 at
   * the top of `LobbyScreen.tsx`.
   */
  colorEditable: boolean;
  /** Colours other players already wear, so nobody picks one twice. */
  takenColors: readonly string[];
  /** Teams are on and this player is on one, so their own colour decides nothing. */
  colorIsSecondary: boolean;
  /** Whether the name field takes focus on open. See the note above. */
  autoFocusName: boolean;
  /** Fires on the press, every press. A colour costs one field on one player. */
  onPickColor: (color: string) => void;
  /**
   * Every way out of the sheet, carrying the cleaned name — or null when there
   * is nothing to send, which is a name that has not changed and a name that is
   * not a name. The caller closes the sheet on this and does not get a say in
   * whether it closes: out means out.
   */
  onDone: (name: string | null) => void;
}): ReactElement {
  const [name, setName] = useState(currentName);
  const [color, setColor] = useState(currentColor);
  const [reason, setReason] = useState<string | null>(null);

  // `name` is a dependency rather than a ref, so the Escape listener below is
  // rebuilt on every keystroke. That is a `removeEventListener` and an
  // `addEventListener` per letter, which is nothing — and the ref version of
  // this was written first and is what `react-hooks` refuses: reading a ref
  // during render is exactly the shape it forbids.
  const done = useCallback(() => {
    const check = validateDisplayName(name);
    // Unchanged, or unusable. Either way there is nothing to send and the old
    // name stands; the field has been saying why for an invalid one the whole
    // time it was being typed.
    if (!check.ok || check.value === currentName) {
      onDone(null);
      return;
    }
    onDone(check.value);
  }, [name, currentName, onDone]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') done();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [done]);

  const pickColor = (next: string) => {
    setColor(next);
    // On the press. There is no Cancel to undo it and nothing to hold it for.
    onPickColor(next);
  };

  return createPortal(
    <div
      data-testid="edit-player-scrim"
      className="fixed inset-0 z-[35] flex items-end justify-center p-4 sm:items-center"
      style={{ background: 'var(--c-scrim)' }}
      // The darkened area is a way out, which it was not before — Isaac expects
      // a scrim to be one, and with nothing to discard there is no reason it
      // should not be. `swallowNextClick` for the reason every scrim in this app
      // now has it: closing on the press unmounts this before the finger comes
      // up, so the click would land on whatever is underneath — here, the lobby
      // row and its three-dots button.
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        swallowNextClick();
        done();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-player-title"
        className="sheet-up flex w-full max-w-[380px] flex-col gap-3 rounded-frame border border-line bg-surface p-4 sm:p-5"
      >
        <h2 id="edit-player-title" className="text-xl font-bold">
          You in this room
        </h2>

        <input
          autoFocus={autoFocusName}
          value={name}
          maxLength={DISPLAY_NAME_MAX}
          aria-label="Your name"
          aria-invalid={reason !== null || undefined}
          onChange={(event) => {
            const typed = event.target.value;
            setName(typed);
            // Live, because there is no longer a Save to spring it on. With
            // every way out of the sheet keeping the edit, a name that cannot be
            // kept has to say so while it is being typed — otherwise the first
            // anybody hears of it is that their rename silently did not happen.
            const check = validateDisplayName(typed);
            setReason(check.ok ? null : check.error);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            done();
          }}
          className="min-h-12 rounded-[11px] border border-line bg-surface-2 px-3 text-[17px] text-ink"
        />

        {/* Reserved whether or not there is anything to say, so a rejected name
            does not push the colours and the buttons down under the thumb
            already reaching for them. */}
        <p role="alert" className="min-h-[18px] text-[13px] text-alert">
          {reason}
        </p>

        {color === null ? (
          <p data-testid="no-colours" className="text-[13px] text-ink-muted">
            No colours in a room this size. Everybody goes by name.
          </p>
        ) : !colorEditable ? (
          <p data-testid="colour-not-yours" className="text-[13px] text-ink-muted">
            The host is picking the colours in this room.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <SectionLabel>your colour</SectionLabel>
              {colorIsSecondary ? (
                // The one thing the picker cannot say for itself: while teams are
                // on, this decides nothing anybody in the room can see.
                <span className="ml-auto text-[12px] text-ink-muted">your team decides it</span>
              ) : null}
            </div>
            <ColorPicker
              value={color}
              onChange={pickColor}
              taken={takenColors}
              secondary={colorIsSecondary}
            />
          </>
        )}

        {/* One button, and it is not `Save` — there is nothing to save that has
            not already happened or is not about to. `Done` says the sheet is
            finished with, which is what pressing it means. */}
        <Button variant="primary" onClick={done} block>
          Done
        </Button>
      </div>
    </div>,
    document.body,
  );
}
